import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  GetMessageWithIntl,
  LoadingIcon,
  Toast,
  error,
  success,
} from '~/shared/components';
import {
  Property,
  Preset,
  getCommaTypeNumberSchema,
  MenuActionItem,
  ViewMenu,
  ListView,
  ConfirmationDialog,
} from '~/shared/components/ui';
import { useAuth } from '~/shared/contexts/AuthProvider';
import { mtechnavi } from '~/shared/libs/clientsdk';
import { PresetItem } from '~/shared/services';
import {
  FullMethodName_ListBusinessUnitFacilitiess,
  FullMethodName_ListBusinessUnitBranchs,
  PageState,
  ViewId,
  getExceptionMessage,
  getPresetAndSchema,
  saveLocalStorageCheckboxData,
  getWorkerExceptionMessage,
  actionType,
} from '~/shared/utils';
import { AggregateStage } from '~/worker';
import {
  FacilityDialog,
  FacilityFormOption,
} from '~/shared/components/ui/Dialog/FacilityDialog';

const VIEW_ID: ViewId = 'OCP_FACILITIES_LIST';
type BusinessUnitFacilities = mtechnavi.api.company.BusinessUnitFacilities;

export function FacilitiesList() {
  const [isLoading, setLoading] = useState(false);
  const myEmail = useAuth().user?.email ?? '';
  const intl = useIntl();
  const navi = useNavigate();
  const [childrenPresetItem, setChildrenPresetItem] = useState<PresetItem[]>();
  const [schema, setSchema] = useState<Property[]>([]);
  const [preset, setPreset] = useState<Preset>({
    filter: {},
    propertyNames: [],
  });

  const [menuModalIsOpen, setMenuModalIsOpen] = useState(false);
  const [isFacilitiesDialogOpen, setFacilitiesDialogOpen] = useState(false);
  const [isReload, setReload] = useState(false);
  const deleteMessage = {
    id: 'C0000001',
    value: { $1: '「削除」' },
  };

  const businessUnitFacilitiesList = useRef<BusinessUnitFacilities[]>([]);
  const deleteIds = useRef<string[]>([]);
  const toastSuccess = GetMessageWithIntl(intl, { id: 'I0000001' });

  const facilityFormOption = useRef<FacilityFormOption>({});
  const FieldName_MergedBusinessUnitBranchs = 'mergedBusinessUnitBranchs';
  const aggregateStages: AggregateStage[] = [
    {
      $lookup: {
        from: FullMethodName_ListBusinessUnitBranchs,
        localField: 'businessUnitBranchId',
        foreignField: 'businessUnitBranchId',
        as: FieldName_MergedBusinessUnitBranchs,
        option: {
          useQuery: false, // リロードしない
        },
      },
    },
  ];

  useEffect(() => {
    (async () => {
      try {
        // スキーマ情報、preset関係の情報を取得
        const { childrenPresetItem, schemas, preset } =
          await getPresetAndSchema(VIEW_ID, [
            FullMethodName_ListBusinessUnitFacilitiess,
            FullMethodName_ListBusinessUnitBranchs,
          ]);

        const listBusinessUnitFacilitiessSchemas = schemas[0]!;
        const listBusinessUnitBranchsSchemas = schemas[1]!;

        const mergedBusinessUnitBranchsSchemas =
          listBusinessUnitBranchsSchemas.map((v) => {
            return {
              name: `${FieldName_MergedBusinessUnitBranchs}.${v.name}`,
              typeName: v.typeName,
            };
          });
        const commaSch = getCommaTypeNumberSchema(
          [
            ...listBusinessUnitFacilitiessSchemas,
            ...mergedBusinessUnitBranchsSchemas,
          ],
          ['assetsAmount']
        );

        // 取得した情報をセット
        setChildrenPresetItem(childrenPresetItem);
        setSchema(commaSch);
        setPreset(preset);
      } catch (err) {
        error(getExceptionMessage(intl, err));
        throw err;
      }
    })();
  }, [intl]);

  // メニューの「登録」処理
  const handleMenuInput = (prop?: string[]) => {
    const state: PageState = {
      ids: prop ?? [],
      sourceViewId: VIEW_ID,
      actionType: 'add',
      baseViewOption: { sourceViewId: VIEW_ID },
    };
    navi('/facilities-input', { state });
  };

  // メニューの「確認」処理
  const handleMenuConfirm = (prop?: string[]) => {
    const items = businessUnitFacilitiesList.current.filter((item) =>
      (prop ?? []).includes(item.businessUnitFacilitiesId)
    );

    facilityFormOption.current = {
      buFacilities: items[0],
      isShared: false,
    };
    setFacilitiesDialogOpen(true);
  };

  // メニューの「編集」処理
  const handleMenuEdit = (type: actionType, prop?: string[]) => {
    if ((prop ?? []).length > 1) {
      error([GetMessageWithIntl(intl, { id: 'E0000073' })]);
      return;
    }
    const state: PageState = {
      ids: prop ?? [],
      sourceViewId: VIEW_ID,
      actionType: type,
      baseViewOption: { sourceViewId: VIEW_ID },
    };
    navi('/facilities-input', { state });
  };

  const clearCheckBox = useCallback(() => {
    saveLocalStorageCheckboxData(VIEW_ID, [], myEmail);
  }, [myEmail]);

  const handleMenuDelete = async () => {
    const deleteItems = businessUnitFacilitiesList.current.filter((item) =>
      deleteIds.current.includes(item.businessUnitFacilitiesId)
    );
    if (deleteItems.length === 0) {
      console.error('No Data');
      return;
    }
    setLoading(true);
    setReload(false);
    try {
      await window.App.services.ui.worker.apiCall({
        actionName: 'deleteBusinessUnitFacilities',
        request: deleteItems[0],
      });
      success([toastSuccess]);
      clearCheckBox();
      setReload(true);
    } catch (err) {
      error(getWorkerExceptionMessage(intl, err));
    }
    setMenuModalIsOpen(false);
    setLoading(false);
  };

  const setMenuActionItem = (): MenuActionItem[] => {
    const menuActionItems: MenuActionItem[] = [];
    menuActionItems.push({
      menuActionType: 'headerIconMenu',
      menu: headerIconEvent(),
      maxMenuColumn: 5,
    });
    menuActionItems.push({
      menuActionType: 'listIconMenu',
      menu: listIconEvent(),
    });
    return menuActionItems;
  };

  const headerIconEvent = (): ViewMenu[] => {
    const menuItems: ViewMenu[] = [];
    // 追加
    menuItems.push({
      name: 'noteadd',
      func: () => handleMenuInput(),
    });
    // コピー
    menuItems.push({
      name: 'copy',
      func: (v?: string[]) => handleMenuEdit('copy', v),
    });
    // 削除
    menuItems.push({
      name: 'delete',
      func: (v?: string[]) => {
        deleteIds.current = v as string[];

        if (deleteIds.current.length > 1) {
          error([GetMessageWithIntl(intl, { id: 'E0000073' })]);
          return;
        }
        setMenuModalIsOpen(true);
      },
    });
    return menuItems;
  };

  const listIconEvent = (): ViewMenu[] => {
    const menuItems: ViewMenu[] = [];
    // 確認
    menuItems.push({
      name: 'description',
      func: (v?: string[]) => handleMenuConfirm(v),
    });
    // 編集
    menuItems.push({
      name: 'edit',
      func: (v?: string[]) => handleMenuEdit('edit', v),
    });

    return menuItems;
  };

  const onOriginalItem = (items: unknown) => {
    businessUnitFacilitiesList.current =
      items as unknown as BusinessUnitFacilities[];
  };

  return (
    <>
      <Container viewId={VIEW_ID}>
        <div className="FacilitiesList">
          <ListView
            pageInfo={{
              schema: schema,
              preset: preset,
              menuItem: setMenuActionItem(),
              menuTarget: 'businessUnitFacilitiesId',
              headerTitle: { viewId: VIEW_ID },
              presetItems: childrenPresetItem,
              listSkipType: {
                isTotal: true,
                isOutput: true,
                isListActionMenu: true,
              },
            }}
            isReload={isReload}
            fullMethodName={FullMethodName_ListBusinessUnitFacilitiess}
            stateOption={{
              onOriginalItemState: onOriginalItem,
            }}
            filterItemOption={{
              aggregateStages: aggregateStages,
              isRequestBodyFilter: true,
            }}
          />
          <ConfirmationDialog
            isOpen={menuModalIsOpen}
            viewMessage={deleteMessage}
            messageLabelId={{ prefixId: 'DIALOG_MESSAGE', viewId: VIEW_ID }}
            onDecision={() => {
              handleMenuDelete();
            }}
            onCancel={() => setMenuModalIsOpen(false)}
          />
          <FacilityDialog
            isOpen={isFacilitiesDialogOpen}
            inputOption={facilityFormOption.current}
            onCancel={() => {
              setFacilitiesDialogOpen(false);
            }}
          />
        </div>
        <Toast />
      </Container>
      {isLoading && <LoadingIcon />}
    </>
  );
}
